import React, { useState, useRef, useEffect } from "react";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import {
  Plus, Download, Upload, Loader2, Users,
  FileDown, FileSpreadsheet, FileSignature, Info, X,
  ChevronDown, RefreshCw, UserPlus, Check, FileText,
  RotateCcw, UserCheck,
} from "lucide-react";

function ManualImportHelp({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-card rounded-xl shadow-xl max-w-lg w-full mx-4 p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>
        <h3 className="text-base font-semibold mb-1 flex items-center gap-2 text-statusWarning">
          <FileSignature className="h-4 w-4" /> Formato do XLSX — Assinaturas Manuais
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          O arquivo deve ter uma linha de cabeçalho com as colunas abaixo. Colunas extras são ignoradas.
        </p>
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Coluna</th>
                <th className="px-3 py-2 text-left">Obrigatório</th>
                <th className="px-3 py-2 text-left">Descrição</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr>
                <td className="px-3 py-2 font-mono font-semibold text-foreground">CPF</td>
                <td className="px-3 py-2"><span className="text-emerald-600 font-medium">Sim (preferencial)</span></td>
                <td className="px-3 py-2 text-muted-foreground">CPF do cedente — com ou sem pontuação</td>
              </tr>
              <tr className="bg-muted/50">
                <td className="px-3 py-2 font-mono font-semibold text-foreground">Nome</td>
                <td className="px-3 py-2"><span className="text-amber-600 font-medium">Fallback</span></td>
                <td className="px-3 py-2 text-muted-foreground">Usado se o CPF não encontrar o cedente</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-mono font-semibold text-foreground">Tipo Documento</td>
                <td className="px-3 py-2 text-muted-foreground">Não</td>
                <td className="px-3 py-2 text-muted-foreground">
                  <span className="font-mono text-xs">termo</span> · <span className="font-mono text-xs">cessao</span> · <span className="font-mono text-xs">anuencia</span> · <span className="font-mono text-xs">honorarios</span> · <span className="font-mono text-xs">outro</span>{" "}
                  <span className="text-muted-foreground">(padrão: outro)</span>
                </td>
              </tr>
              <tr className="bg-muted/50">
                <td className="px-3 py-2 font-mono font-semibold text-foreground">Data Assinatura</td>
                <td className="px-3 py-2 text-muted-foreground">Não</td>
                <td className="px-3 py-2 text-muted-foreground">Data da assinatura física (padrão: hoje)</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-mono font-semibold text-foreground">Observação</td>
                <td className="px-3 py-2 text-muted-foreground">Não</td>
                <td className="px-3 py-2 text-muted-foreground">Texto livre para anotações</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          A unicidade é por <strong>cedente + processo + tipo de documento</strong> — o mesmo cedente pode ter até 5 registros (um por tipo).
          Combinações já existentes são ignoradas.
        </p>
      </div>
    </div>
  );
}

/* Botão de upload simples — encapsula input hidden + label */
function FileUploadButton({ id, accept, onChange, disabled, loading, color = "default", children }) {
  const colorMap = {
    default: "border-border text-muted-foreground hover:bg-muted hover:border-border",
    amber:   "border-statusWarning/20 text-statusWarning hover:bg-statusWarningBg hover:border-statusWarning/30",
  };
  return (
    <>
      <input type="file" accept={accept} className="hidden" id={id} onChange={onChange} disabled={disabled} />
      <Label htmlFor={id} className="cursor-pointer">
        <div className={`
          inline-flex items-center justify-center gap-1.5 h-9 px-3 rounded-md border text-sm font-medium
          transition-colors duration-150 select-none
          ${disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : "cursor-pointer"}
          ${colorMap[color]}
        `}>
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : children}
        </div>
      </Label>
    </>
  );
}

/* Popover de importação de propostas com seleção de modo */
function ImportPropostasButton({
  onImportXLSX, isImportingXLSX,
  criarSeNaoExistir, onToggleCriar,
  resetarAssinados, onToggleResetar,
  definirAtivo, onSetDefinirAtivo,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  function handleSelectFile() {
    setOpen(false);
    fileRef.current?.click();
  }

  const MODES = [
    {
      value: false,
      icon: RefreshCw,
      label: "Apenas atualizar existentes",
      desc: "Atualiza valores de cedentes já cadastrados. Novos nomes são ignorados.",
    },
    {
      value: true,
      icon: UserPlus,
      label: "Atualizar + criar novos",
      desc: "Cria cedentes inexistentes com nome e CPF da planilha.",
    },
  ];

  return (
    <div className="relative" ref={ref}>
      {/* Input oculto — disparado programaticamente */}
      <input
        ref={fileRef}
        type="file"
        accept=".xlsx"
        className="hidden"
        onChange={(e) => { onImportXLSX(e); setOpen(false); }}
        disabled={isImportingXLSX}
      />

      {/* Botão principal */}
      <button
        type="button"
        disabled={isImportingXLSX}
        onClick={() => setOpen((v) => !v)}
        className={`
          inline-flex items-center gap-1.5 h-9 px-3 rounded-md border text-sm font-medium
          transition-colors duration-150 select-none cursor-pointer
          border-statusSuccess/20 text-statusSuccess hover:bg-statusSuccessBg hover:border-statusSuccess/30
          ${open ? "bg-statusSuccessBg border-statusSuccess/30" : "bg-card"}
          ${isImportingXLSX ? "opacity-50 cursor-not-allowed" : ""}
        `}
      >
        {isImportingXLSX
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <FileSpreadsheet className="h-3.5 w-3.5" />}
        Importar Propostas
        <ChevronDown className={`h-3 w-3 ml-0.5 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </button>

      {/* Popover */}
      {open && (
        <div className="absolute right-0 top-full mt-1.5 z-30 w-72 rounded-xl border border-border bg-card shadow-lg shadow-slate-200/60 overflow-hidden">
          <div className="px-4 pt-3 pb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Modo de importação</p>
          </div>

          <div className="px-2 pb-2 flex flex-col gap-1">
            {MODES.map(({ value, icon: Icon, label, desc }) => {
              const selected = criarSeNaoExistir === value;
              return (
                <button
                  key={String(value)}
                  type="button"
                  onClick={() => onToggleCriar && onToggleCriar(value)}
                  className={`
                    w-full flex items-start gap-3 px-3 py-2.5 rounded-lg text-left transition-colors duration-150
                    ${selected
                      ? "bg-statusSuccessBg ring-1 ring-emerald-200"
                      : "hover:bg-muted"}
                  `}
                >
                  <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${selected ? "bg-statusSuccessBg text-statusSuccess" : "bg-muted text-muted-foreground"}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className={`block text-sm font-medium ${selected ? "text-statusSuccess" : "text-foreground"}`}>
                      {label}
                    </span>
                    <span className="block text-xs text-muted-foreground mt-0.5 leading-snug">{desc}</span>
                  </span>
                  {selected && <Check className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />}
                </button>
              );
            })}
          </div>

          {/* Secao: Opcoes avancadas */}
          <div className="border-t border-border px-4 pt-3 pb-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Opcoes avancadas</p>

            {/* Toggle: resetar assinados */}
            <label className="flex items-start gap-3 py-2 cursor-pointer group">
              <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground group-hover:bg-orange-50 group-hover:text-orange-500 transition-colors">
                <RotateCcw className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground">Resetar status de assinados</span>
                <span className="block text-xs text-muted-foreground mt-0.5 leading-snug">
                  Cedentes com status "assinado" ou "concluido" voltam para "pendente".
                </span>
              </div>
              <input
                type="checkbox"
                checked={resetarAssinados}
                onChange={(e) => onToggleResetar && onToggleResetar(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border text-orange-600 focus:ring-orange-500"
              />
            </label>

            {/* Select: definir ativo */}
            <div className="flex items-center gap-2 py-2">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <UserCheck className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground mb-1">Status de acesso</span>
                <select
                  value={definirAtivo === null ? "" : String(definirAtivo)}
                  onChange={(e) => {
                    const v = e.target.value;
                    onSetDefinirAtivo && onSetDefinirAtivo(v === "" ? null : v === "true");
                  }}
                  className="w-full h-8 rounded-md border border-border bg-card px-2 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-slate-300"
                >
                  <option value="">Nao alterar</option>
                  <option value="true">Ativar cedentes encontrados</option>
                  <option value="false">Inativar cedentes encontrados</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-border px-3 py-2.5">
            <button
              type="button"
              onClick={handleSelectFile}
              className="w-full flex items-center justify-center gap-2 h-9 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-sm font-medium transition-colors duration-150"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Selecionar arquivo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ModelosDropdown({ onDownloadTemplate, onDownloadTemplatePropostas, onDownloadTemplateManuais }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  const items = [
    { icon: FileText, label: "Modelo XLSX — Cedentes", desc: "Importação/atualização de dados cadastrais e financeiros dos cedentes", onClick: onDownloadTemplate },
    { icon: FileSpreadsheet, label: "Modelo XLSX — Propostas", desc: "Importação de valores e propostas (header na linha 8)", onClick: onDownloadTemplatePropostas },
    { icon: FileSignature, label: "Modelo XLSX — Assinaturas Manuais", desc: "Registro de assinaturas físicas em lote", onClick: onDownloadTemplateManuais },
  ];

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(v => !v)}
        className="h-9 text-muted-foreground hover:text-foreground hover:bg-muted"
      >
        <Download className="mr-1.5 h-3.5 w-3.5" />
        Modelos
        <ChevronDown className={`ml-1 h-3 w-3 transition-transform duration-150 ${open ? "rotate-180" : ""}`} />
      </Button>
      {open && (
        <div className="absolute left-0 top-full mt-1.5 z-30 w-72 rounded-xl border border-border bg-card shadow-lg shadow-slate-200/60 overflow-hidden">
          {items.map(({ icon: Icon, label, desc, onClick }) => (
            <button
              key={label}
              type="button"
              onClick={() => { onClick?.(); setOpen(false); }}
              className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted transition-colors duration-150 border-b border-border last:border-0"
            >
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <Icon className="h-3.5 w-3.5" />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-foreground">{label}</span>
                <span className="block text-xs text-muted-foreground mt-0.5 leading-snug">{desc}</span>
              </span>
              <Download className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-1" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function CedenteActions({
  onDownloadTemplate, onDownloadTemplatePropostas, onDownloadTemplateManuais,
  onImportCSV, onImportXLSX, onOpenDialog,
  isSaving, isImportingXLSX, onExportCSV, onExportXLSX, isExporting,
  onImportManualXLSX, isImportingManual,
  criarSeNaoExistir = false, onToggleCriar,
  resetarAssinados = false, onToggleResetar,
  definirAtivo = null, onSetDefinirAtivo,
}) {
  const [showManualHelp, setShowManualHelp] = useState(false);

  return (
    <>
      {showManualHelp && <ManualImportHelp onClose={() => setShowManualHelp(false)} />}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">

        {/* Título */}
        <h3 className="text-xl font-semibold flex items-center gap-2 text-foreground shrink-0">
          <Users className="h-5 w-5 text-muted-foreground" />
          Substituídos Vinculados
        </h3>

        {/* Ações */}
        <div className="flex flex-wrap items-center gap-2">

          {/* Grupo: exportar / modelos */}
          <div className="flex items-center gap-1.5">
            <Button
              variant="ghost"
              size="sm"
              onClick={onExportXLSX || onExportCSV}
              disabled={isExporting}
              className="h-9 text-muted-foreground hover:text-foreground hover:bg-muted"
            >
              {isExporting
                ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <FileDown className="mr-1.5 h-3.5 w-3.5" />}
              Exportar
            </Button>

            <ModelosDropdown
              onDownloadTemplate={onDownloadTemplate}
              onDownloadTemplatePropostas={onDownloadTemplatePropostas}
              onDownloadTemplateManuais={onDownloadTemplateManuais}
            />
          </div>

          {/* Divisor */}
          <div className="h-6 w-px bg-muted" />

          {/* Grupo: importações */}
          <div className="flex items-center gap-1.5">

            {/* Importar Cedentes */}
            <FileUploadButton
              id="csv-upload"
              accept=".csv,.xlsx"
              onChange={onImportCSV}
              disabled={isSaving}
              loading={isSaving}
            >
              <Upload className="h-3.5 w-3.5" />
              Importar Cedentes
            </FileUploadButton>

            {/* Importar Propostas — popover com seleção de modo */}
            <ImportPropostasButton
              onImportXLSX={onImportXLSX}
              isImportingXLSX={isImportingXLSX}
              criarSeNaoExistir={criarSeNaoExistir}
              onToggleCriar={onToggleCriar}
              resetarAssinados={resetarAssinados}
              onToggleResetar={onToggleResetar}
              definirAtivo={definirAtivo}
              onSetDefinirAtivo={onSetDefinirAtivo}
            />

            {/* Importar Assinaturas Manuais */}
            {onImportManualXLSX && (
              <div className="flex items-center gap-0 rounded-md border border-statusWarning/20 overflow-hidden">
                <FileUploadButton
                  id="manual-xlsx-upload"
                  accept=".xlsx"
                  onChange={onImportManualXLSX}
                  disabled={isImportingManual}
                  loading={isImportingManual}
                  color="amber"
                >
                  <FileSignature className="h-3.5 w-3.5" />
                  Assinaturas Manuais
                </FileUploadButton>
                <button
                  type="button"
                  title="Ver formato esperado do arquivo"
                  onClick={() => setShowManualHelp(true)}
                  className="flex items-center justify-center w-9 h-9 border-l border-statusWarning/20 text-amber-500 hover:bg-statusWarningBg transition-colors duration-150 bg-card"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Divisor */}
          <div className="h-6 w-px bg-muted" />

          {/* CTA principal */}
          <Button size="sm" className="h-9" onClick={() => onOpenDialog(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Adicionar Substituído
          </Button>

        </div>
      </div>
    </>
  );
}
